import { useEffect, useState } from "react";
import { env } from "../utils/env";

// `env()` reads `window.env` (runtime, from `public/env.js` — regenerated from
// AWS Parameter Store by the deploy workflow) first, and falls back to
// `process.env` (build-time, set by CRA from `.env`). So both deploy-time and
// local-dev env mechanisms are honoured.

export const useMetaSdk = () => {
  const [fbReady, setFbReady] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const appId = env("REACT_APP_META_APP_ID");

    // Diagnostic: confirms the exact value (and length/charset) reaching FB.init.
    // If you see anything other than a string of digits here, the env wiring is
    // still mangling the value. If you see a clean digit string but Meta still
    // rejects it as "Invalid app ID", the problem is on the Meta dashboard side
    // (app deleted/disabled, wrong app type, or your FB user not a dev/tester).
    // eslint-disable-next-line no-console
    console.log("[useMetaSdk] appId =", JSON.stringify(appId), "len =", appId.length);

    if (!appId) {
      // Bail with a clear error instead of letting Meta reject a placeholder
      // app id later with the cryptic "Invalid app ID" message.
      const msg =
        "Meta App ID is not configured. Set REACT_APP_META_APP_ID in .env (local — restart `npm start` after editing) or container env (deployed).";
      console.error("[useMetaSdk]", msg);
      setError(msg);
      return;
    }

    const initialize = () => {
      if (!window.FB) return;
      window.FB.init({
        appId,
        cookie: true,
        xfbml: true,
        version: "v22.0",
        status: true,
      });
      window.FB.getLoginStatus(() => setFbReady(true));
    };

    if (!document.getElementById("facebook-jssdk")) {
      const script = document.createElement("script");
      script.id = "facebook-jssdk";
      script.src = "https://connect.facebook.net/en_US/sdk.js";
      script.async = true;
      script.defer = true;
      script.onload = initialize;
      script.onerror = () =>
        setError("Failed to load Facebook SDK. Check your internet connection or try again later.");
      document.body.appendChild(script);
    } else {
      initialize();
    }
  }, []);

  return { fbReady, error };
};
